import React, { useEffect, useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';

function CameraModal({ isOpen, title = 'Take Photo', onCapture, onClose }) {
  const webcamRef   = useRef(null);
  const [captured,  setCaptured]  = useState(null);
  const [camReady,  setCamReady]  = useState(false);
  const [camError,  setCamError]  = useState(null);
  const [facing,    setFacing]    = useState('environment');

  useEffect(() => {
    if (isOpen) {
      window.__cameraOpen = true;
    } else {
      window.__cameraOpen = false;
    }
    return () => {
      window.__cameraOpen = false;
    };
  }, [isOpen]);

  const videoConstraints = { facingMode: facing, width: 1280, height: 720 };

  const handleCapture = useCallback(() => {
    const screenshot = webcamRef.current?.getScreenshot();
    if (screenshot) setCaptured(screenshot);
  }, []);

  const handleRetake = () => setCaptured(null);

  const handleConfirm = () => {
    if (!captured) {
      return;
    }

    fetch(captured)
      .then((r) => r.blob())
      .then((blob) => {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file, captured);
        setCaptured(null);
      });
  };

  const handleClose = () => {
    setCaptured(null);
    setCamReady(false);
    setCamError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>

        {/* ── Header ── */}
        <div style={styles.header}>
          <button style={styles.closeBtn} onClick={handleClose}>✕</button>
          <span style={styles.headerTitle}>{title}</span>
          {!captured && (
            <button
              style={styles.flipBtn}
              onClick={() => setFacing((f) => (f === 'environment' ? 'user' : 'environment'))}
              title="Flip camera"
            >
              🔄
            </button>
          )}
          {captured && <span style={{ width: 36 }} />}
        </div>

        {/* ── Camera / Preview ── */}
        <div style={styles.viewport}>
          {!captured ? (
            <>
              {camError ? (
                <div style={styles.errorBox}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                  <div style={{ fontSize: 13, color: '#c0392b', textAlign: 'center' }}>
                    Camera access denied or unavailable.<br />
                    Check browser permissions.
                  </div>
                </div>
              ) : (
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  screenshotQuality={0.92}
                  videoConstraints={videoConstraints}
                  onUserMedia={() => setCamReady(true)}
                  onUserMediaError={(err) => setCamError(String(err))}
                  style={styles.webcam}
                />
              )}

              {!camReady && !camError && (
                <div style={styles.loadingOverlay}>
                  <div style={{ fontSize: 13, color: '#fff' }}>Opening camera…</div>
                </div>
              )}

              {/* Shutter outline guide */}
              {camReady && (
                <div style={styles.focusFrame} />
              )}
            </>
          ) : (
            <img src={captured} alt="Captured" style={styles.preview} />
          )}
        </div>

        {/* ── Actions ── */}
        <div style={styles.actions}>
          {!captured ? (
            <button
              style={{ ...styles.shutterBtn, opacity: camReady ? 1 : 0.4 }}
              disabled={!camReady}
              onClick={handleCapture}
            >
              <div style={styles.shutterInner} />
            </button>
          ) : (
            <div style={styles.confirmRow}>
              <button style={styles.retakeBtn} onClick={handleRetake}>↩ Retake</button>
              <button style={styles.useBtn}    onClick={handleConfirm}>✓ Use Photo</button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ── Inline styles (no CSS file dependency) ────────────────────────────────────
const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  modal: {
    width: '100%', maxWidth: 480, background: '#111',
    borderRadius: 18, overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    maxHeight: '95dvh',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', background: '#1a1a1a',
  },
  headerTitle: {
    color: '#fff', fontSize: 15, fontWeight: 600, fontFamily: "'Poppins', sans-serif",
  },
  closeBtn: {
    background: 'transparent', border: 'none', color: '#aaa',
    fontSize: 18, cursor: 'pointer', width: 36, height: 36,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '50%',
  },
  flipBtn: {
    background: 'transparent', border: 'none', fontSize: 20,
    cursor: 'pointer', width: 36, height: 36,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  viewportWrap: { position: 'relative' },
  viewportContainer: { position: 'relative' },
  viewportOuter: { position: 'relative', background: '#000' },
  viewportFrame: { position: 'relative' },
  viewportMain: { position: 'relative' },
  viewportArea: { position: 'relative' },
  viewportCore: { position: 'relative' },
  viewportBox: { position: 'relative' },
  viewportShell: { position: 'relative' },
  viewport: { position: 'relative', background: '#000', overflow: 'hidden' },
  webcam: {
    width: '100%', display: 'block', maxHeight: '60dvh', objectFit: 'cover',
  },
  preview: {
    width: '100%', display: 'block', maxHeight: '60dvh', objectFit: 'contain',
    background: '#000',
  },
  loadingOverlay: {
    position: 'absolute', inset: 0, display: 'flex',
    alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)',
  },
  focusFrame: {
    position: 'absolute',
    top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 200, height: 200,
    border: '2px solid rgba(255,255,255,0.35)',
    borderRadius: 12, pointerEvents: 'none',
  },
  errorBox: {
    padding: 32, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', minHeight: 220,
    background: '#1a0000',
  },
  actions: {
    padding: '16px 20px 20px', display: 'flex',
    alignItems: 'center', justifyContent: 'center', background: '#1a1a1a',
  },
  shutterBtn: {
    width: 68, height: 68, borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)', border: '3px solid #fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'transform 0.1s',
  },
  shutterInner: {
    width: 52, height: 52, borderRadius: '50%', background: '#fff',
  },
  confirmRow: {
    display: 'flex', gap: 12, width: '100%',
  },
  retakeBtn: {
    flex: 1, padding: '12px 0', border: '2px solid rgba(255,255,255,0.3)',
    borderRadius: 12, background: 'transparent', color: '#fff',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Poppins', sans-serif",
  },
  useBtn: {
    flex: 2, padding: '12px 0', border: 'none',
    borderRadius: 12, background: '#6B21A8', color: '#fff',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Poppins', sans-serif",
  },
};

export default CameraModal;